<template>
    <v-main>
        <div class="envoy-doc">

                <!-- Header / actions -->
                <div class="envoy-doc__header no-print">
                    <div class="envoy-doc__header-text">
                        <div class="text-h4 font-weight-bold">Envoy&nbsp;&harr;&nbsp;Cin7 Integration</div>
                        <div class="text-subtitle-1 grey--text text--darken-1">
                            Shareable copy &mdash; internal identifiers redacted.
                        </div>
                    </div>
                    <v-btn color="primary" @click="printDoc" class="flex-shrink-0">
                        <v-icon left>mdi-file-pdf-box</v-icon>
                        Download&nbsp;PDF
                    </v-btn>
                </div>

                <!-- Print-only title -->
                <div class="print-only mb-4">
                    <div class="text-h4 font-weight-bold">Envoy &harr; Cin7 Integration</div>
                    <div class="text-subtitle-2 grey--text text--darken-1">
                        Shareable copy &mdash; internal identifiers redacted
                    </div>
                </div>

                <v-alert dense text type="warning" border="left" class="mt-2 no-print">
                    <strong>Shareable version.</strong> This mirrors our internal integration doc with
                    sensitive values removed &mdash; internal infrastructure names, database / Cin7 field
                    identifiers, and a customer reference are shown as <code>[redacted]</code>. For the freight
                    problem specifically, see <strong>Known Issues</strong> (section 8).
                </v-alert>

                <v-alert dense text type="info" border="left" class="mt-2">
                    <strong>TL;DR.</strong> Dealers place wholesale orders in <em>Envoy</em>. Those orders flow
                    into <em>Cin7</em> (the system of record) as sales orders, then status / fulfillment / pricing
                    changes are written back out to Envoy so dealers see the latest. Everything moves as
                    <strong>CSV files over SFTP</strong> on a schedule &mdash; there is no live API link between the
                    two platforms.
                </v-alert>

                <!-- ============ 1. THE SYSTEMS ============ -->
                <v-card outlined class="mt-6">
                    <v-card-title class="text-h5">1.&nbsp;&nbsp;The Systems</v-card-title>
                    <v-divider />
                    <v-card-text>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th>System</th>
                                        <th>Role</th>
                                        <th>How we talk to it</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(s, i) in systems" :key="i">
                                        <td class="font-weight-medium">{{ s.name }}</td>
                                        <td>{{ s.role }}</td>
                                        <td>{{ s.access }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>

                        <p class="mt-4 mb-1 font-weight-medium">Two Envoy instances</p>
                        <p class="mb-0">
                            Envoy is split into two storefronts. Regular wholesale dealers live on the
                            <strong>Matador</strong> instance (a.k.a. <em>Envoy1</em>); distributors (a distinct
                            account type) live on the <strong>Matador2</strong> instance (<em>Envoy2</em>). Each has
                            its own SFTP login, and the account / order jobs pick the instance based on the account
                            type.
                        </p>
                    </v-card-text>
                </v-card>

                <!-- ============ 2. SFTP LAYOUT ============ -->
                <v-card outlined class="mt-6">
                    <v-card-title class="text-h5">2.&nbsp;&nbsp;The SFTP Exchange</v-card-title>
                    <v-divider />
                    <v-card-text>
                        <p>
                            All traffic passes through Envoy's managed SFTP server
                            (<code>sftp.envoyb2b.com:22</code>). Each integration reads from or writes to a dedicated
                            folder. The server allows <strong>one session per login at a time</strong>, so concurrent
                            jobs on the same account collide.
                        </p>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th>Folder</th>
                                        <th>Direction</th>
                                        <th>Contents</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(f, i) in folders" :key="i">
                                        <td><code>{{ f.path }}</code></td>
                                        <td>
                                            <v-chip x-small :color="f.dir === 'Matador → Envoy' ? 'blue lighten-4' : 'green lighten-4'">
                                                {{ f.dir }}
                                            </v-chip>
                                        </td>
                                        <td>{{ f.contents }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card-text>
                </v-card>

                <!-- ============ 3. ORDER LIFECYCLE ============ -->
                <v-card outlined class="mt-6">
                    <v-card-title class="text-h5">3.&nbsp;&nbsp;Order Lifecycle (round trip)</v-card-title>
                    <v-divider />
                    <v-card-text>
                        <v-timeline dense>
                            <v-timeline-item
                                v-for="(step, i) in lifecycle"
                                :key="i"
                                :color="step.color"
                                small>
                                <div class="font-weight-medium">{{ step.title }}</div>
                                <div class="grey--text text--darken-2" v-html="step.body"></div>
                            </v-timeline-item>
                        </v-timeline>
                    </v-card-text>
                </v-card>

                <!-- ============ 4. ENVOY -> CIN7 ============ -->
                <v-card outlined class="mt-6">
                    <v-card-title class="text-h5">
                        4.&nbsp;&nbsp;Envoy&nbsp;&rarr;&nbsp;Cin7&nbsp;&mdash;&nbsp;New Orders
                    </v-card-title>
                    <v-card-subtitle>
                        Order-import job (distributors handled by a parallel job) &mdash; runs every 5 minutes
                    </v-card-subtitle>
                    <v-divider />
                    <v-card-text>
                        <p>
                            When a dealer submits an order, Envoy drops a file named
                            <code>EnvoyB2B_Order_MTD-####.csv</code> into <code>/sftp/ORDERS</code>. The job reads
                            every matching file, builds a Cin7 sales order, and <code>POST</code>s it to the Cin7 API.
                        </p>
                        <ol class="mb-4">
                            <li>Archive the raw CSV to internal storage (<code>[redacted]</code>).</li>
                            <li>Confirm the <code>Customer Code</code> exists in our contacts; if not, the order still
                                imports but an <strong>orphan-customer warning email</strong> goes to Ops.</li>
                            <li>Resolve the <strong>collection / catalog</strong> from the catalog list (Envoy1
                                source). A name containing &ldquo;preseason&rdquo; routes the order to the
                                <strong>US Preorders</strong> branch and flags it as a preorder; everything else goes to
                                the <strong>US B2B</strong> branch.</li>
                            <li>Look up the <strong>Shipping Account</strong> and <strong>external sales rep</strong>
                                from the Matador database and stamp them onto Cin7 custom fields.</li>
                            <li>Create the order at stage <strong>&ldquo;For Review&rdquo;</strong>, then write back a
                                deep-link to the order in a Cin7 custom field.</li>
                            <li>Delete the file from <code>/sftp/ORDERS</code> so it is not re-imported.</li>
                        </ol>

                        <p class="font-weight-medium mb-1">Field mapping &mdash; CSV column &rarr; Cin7 sales order</p>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th>Envoy CSV column</th>
                                        <th>Cin7 field</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(m, i) in exportFields" :key="i">
                                        <td><code>{{ m.from }}</code></td>
                                        <td><code>{{ m.to }}</code></td>
                                        <td>{{ m.note }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>

                        <p class="font-weight-medium mt-4 mb-1">Line items (one CSV row per SKU)</p>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr><th>Envoy CSV column</th><th>Cin7 line field</th><th>Notes</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(m, i) in exportLineFields" :key="i">
                                        <td><code>{{ m.from }}</code></td>
                                        <td><code>{{ m.to }}</code></td>
                                        <td>{{ m.note }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card-text>
                </v-card>

                <!-- ============ 5. CIN7 -> ENVOY ============ -->
                <v-card outlined class="mt-6">
                    <v-card-title class="text-h5">
                        5.&nbsp;&nbsp;Cin7&nbsp;&rarr;&nbsp;Envoy&nbsp;&mdash;&nbsp;Order Write-Back
                    </v-card-title>
                    <v-card-subtitle>
                        Order write-back job &mdash; runs every 5 minutes
                    </v-card-subtitle>
                    <v-divider />
                    <v-card-text>
                        <p>
                            On a 5-minute schedule the job asks Cin7 for every sales order on a Matador branch that
                            was modified in the <strong>last 24 hours</strong>. It flattens each order to one CSV row
                            per line item and uploads the file to <code>/sftp/INBOUND_ORDERS</code>, where Envoy
                            ingests it and updates the dealer-facing order. This is how status, fulfillment, invoice,
                            and pricing changes get back to the dealer.
                        </p>
                        <p class="mb-1 font-weight-medium">Status translation</p>
                        <p>
                            Cin7 <code>status = VOID</code> &rarr; <strong>Cancelled</strong>;
                            Cin7 <code>stage = Dispatched</code> &rarr; <strong>Shipped</strong> (and shipped qty is
                            filled in); everything else &rarr; <strong>Ordered</strong>.
                        </p>

                        <p class="font-weight-medium mt-3 mb-1">File naming quirk</p>
                        <p>
                            The file is <code>inboundorders_update.csv</code>, or
                            <code>inboundorders_update_N.csv</code> where <code>N = (file count in folder) − 3</code>
                            (the &minus;3 accounts for Envoy's own <code>processing</code> / <code>processed</code> /
                            <code>reports</code> subfolders). This is why several incrementing files appear during
                            testing even when only one order changed.
                        </p>

                        <p class="font-weight-medium mt-3 mb-1">Field mapping &mdash; Cin7 &rarr; Envoy CSV column</p>
                        <p class="text-caption grey--text text--darken-1 mb-1">
                            Every column the job writes, in exact CSV order (one row is emitted per line item).
                            A <code>Submitter Email</code> column also exists in the code but is currently
                            commented out, so it is not sent.
                        </p>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr><th>Cin7 field</th><th>Envoy CSV column</th><th>Notes</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(m, i) in updateFields" :key="i" :class="m.flag ? 'amber lighten-5' : ''">
                                        <td><code>{{ m.from }}</code></td>
                                        <td><code>{{ m.to }}</code></td>
                                        <td>{{ m.note }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card-text>
                </v-card>

                <!-- ============ 6. CUSTOM FIELDS ============ -->
                <v-card outlined class="mt-6">
                    <v-card-title class="text-h5">6.&nbsp;&nbsp;Cin7 Order Custom Fields</v-card-title>
                    <v-divider />
                    <v-card-text>
                        <p>
                            The integration leans on a handful of Cin7 sales-order custom fields. They are the glue
                            that carries Envoy-specific data through Cin7. The internal field IDs are redacted.
                        </p>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr><th>Field</th><th>Meaning</th><th>Set by</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(c, i) in customFields" :key="i">
                                        <td><code>{{ c.id }}</code></td>
                                        <td>{{ c.meaning }}</td>
                                        <td>{{ c.setBy }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card-text>
                </v-card>

                <!-- ============ 7. ACCOUNT SYNC ============ -->
                <v-card outlined class="mt-6">
                    <v-card-title class="text-h5">7.&nbsp;&nbsp;Account &amp; Catalog Sync</v-card-title>
                    <v-divider />
                    <v-card-text>
                        <p>
                            Before a dealer can order, their account must exist in Envoy. Accounts flow
                            <strong>one way</strong>: CRM / Cin7 &rarr; Envoy, as customer CSVs in
                            <code>/sftp/CUSTOMER</code>. Distributors are sent to the <strong>Matador2</strong>
                            instance; everyone else to <strong>Matador</strong>.
                        </p>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr><th>Job</th><th>Trigger</th><th>What it does</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(a, i) in accountLambdas" :key="i">
                                        <td>{{ a.name }}</td>
                                        <td>{{ a.trigger }}</td>
                                        <td>{{ a.does }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>

                        <p class="font-weight-medium mt-4 mb-1">Who qualifies for Envoy (bulk create filters)</p>
                        <ul class="mb-0">
                            <li>Account is active and on the <strong>Wholesale</strong> channel</li>
                            <li>Excludes <strong>Sales Rep</strong> accounts</li>
                            <li>Not flagged to skip the Envoy import</li>
                            <li>On a price list enabled for Envoy</li>
                            <li>Created in the last 6 months <em>or</em> has placed an order since 2021</li>
                        </ul>
                    </v-card-text>
                </v-card>

                <!-- ============ 8. KNOWN ISSUES ============ -->
                <v-card outlined class="mt-6">
                    <v-card-title class="text-h5">8.&nbsp;&nbsp;Known Issues &amp; Gotchas</v-card-title>
                    <v-divider />
                    <v-card-text>
                        <v-expansion-panels accordion multiple>
                            <v-expansion-panel v-for="(issue, i) in issues" :key="i">
                                <v-expansion-panel-header>
                                    <span>
                                        <v-icon small :color="issue.color" class="mr-2">{{ issue.icon }}</v-icon>
                                        {{ issue.title }}
                                    </span>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <span v-html="issue.body"></span>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                        <!-- Print-friendly expansion of the same content -->
                        <div class="print-only">
                            <div v-for="(issue, i) in issues" :key="'p' + i" class="mb-3">
                                <div class="font-weight-medium">{{ issue.title }}</div>
                                <div class="grey--text text--darken-2" v-html="issue.body"></div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- ============ 9. SCHEDULING & MONITORING ============ -->
                <v-card outlined class="mt-6 mb-4">
                    <v-card-title class="text-h5">9.&nbsp;&nbsp;Scheduling &amp; Monitoring</v-card-title>
                    <v-divider />
                    <v-card-text>
                        <p>
                            Each job runs on a fixed cloud schedule. The cadences below are the <strong>actual</strong>
                            schedule expressions; internal schedule/rule names are redacted. All times are
                            <strong>UTC</strong>.
                        </p>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr><th>Job</th><th>Schedule name</th><th>Cadence</th><th>What it does</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(c, i) in cadences" :key="i">
                                        <td>{{ c.job }}</td>
                                        <td><code>{{ c.rule }}</code></td>
                                        <td><code>{{ c.cadence }}</code></td>
                                        <td>{{ c.desc }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>

                        <p class="font-weight-medium mt-4 mb-1">Monitoring</p>
                        <ul class="mb-3">
                            <li>A run counter is incremented per job type as a basic health signal.</li>
                            <li>Raw inbound Envoy order CSVs are archived to internal storage for audit / replay.</li>
                        </ul>
                    </v-card-text>
                </v-card>

                <div class="text-caption grey--text text--darken-1 text-center mt-6 mb-2">
                    Shareable version &mdash; internal identifiers redacted. Summarizes the integration and may
                    drift over time. Order reference for the freight issue: MTD-1602.
                </div>

        </div>
    </v-main>
</template>

<script>
export default {
    beforeMount() {
        this.$store.dispatch('updateShowWaffle', true)
        document.title = 'Sandbox - Envoy / Cin7 Integration (Shareable)'
        this.$store.dispatch('updateNavbarContent', [
            { title: 'Full', icon: 'mdi-file-document-outline', path: '/Ops/Envoy-Cin7-Integration', notification: false },
            { title: 'Shareable', icon: 'mdi-share-variant-outline', path: '/Ops/Envoy-Cin7-Integration/Share', notification: false }
        ])
        this.$store.dispatch('updateNavbarTitle', 'Envoy / Cin7 Integration')
    },
    methods: {
        printDoc() {
            window.print()
        }
    },
    data() {
        return {
            systems: [
                { name: 'Cin7', role: 'ERP / system of record for inventory, pricing, and sales orders.', access: 'REST API (api.cin7.com/api/v1), Basic auth.' },
                { name: 'Envoy B2B', role: 'Wholesale ordering portal where dealers & reps place orders.', access: 'CSV files over SFTP (sftp.envoyb2b.com). No live API.' },
                { name: 'Matador database', role: 'Internal CRM: accounts, contacts, sales reps, catalogs, branches.', access: 'Accessed directly by the integration jobs.' },
                { name: 'Integration jobs', role: 'Scheduled cloud functions that move data between the systems.', access: 'Serverless (details redacted).' }
            ],
            folders: [
                { path: '/sftp/ORDERS', dir: 'Envoy → Matador', contents: 'New dealer orders (EnvoyB2B_Order_MTD-####.csv). Read then deleted.' },
                { path: '/sftp/INBOUND_ORDERS', dir: 'Matador → Envoy', contents: 'Order create / update feed pushed back to Envoy (inboundorders_*.csv).' },
                { path: '/sftp/CUSTOMER', dir: 'Matador → Envoy', contents: 'Account create / update feed (customer_batch_#.csv, customer_update.csv).' }
            ],
            lifecycle: [
                { color: 'green', title: '0. Account exists in Envoy', body: 'CRM/Cin7 accounts are pushed to Envoy first (see Account Sync). Distributors go to the Matador2 instance.' },
                { color: 'green', title: '1. Dealer places an order in Envoy', body: 'Envoy writes <code>EnvoyB2B_Order_MTD-####.csv</code> into <code>/sftp/ORDERS</code>.' },
                { color: 'green', title: '2. Order imported into Cin7', body: 'The order-import job (every 5 min) maps the CSV to a Cin7 sales order at stage <strong>For Review</strong>, POSTs it, archives the raw file to internal storage, then deletes it from SFTP.' },
                { color: 'blue', title: '3. Order is worked in Cin7', body: 'Ops approves, picks, dispatches, and invoices the order. Status, freight, and line changes happen here.' },
                { color: 'blue', title: '4. Changes written back to Envoy', body: 'The order write-back job (every 5 min) pulls orders modified in the last 24h and uploads an update CSV to <code>/sftp/INBOUND_ORDERS</code> so the dealer sees current status / fulfillment.' }
            ],
            cadences: [
                { job: 'Order import job (regular + distributor)', rule: '[redacted]', cadence: 'rate(5 minutes)', desc: 'Import new dealer orders from /sftp/ORDERS into Cin7.' },
                { job: 'Order write-back job (regular + distributor)', rule: '[redacted]', cadence: 'rate(5 minutes)', desc: 'Write Cin7 order changes back to Envoy via /sftp/INBOUND_ORDERS.' },
                { job: 'Order-status sync job', rule: '[redacted]', cadence: 'cron(30 16-23 ? * MON-FRI *)', desc: 'Order-status sync, hourly at :30 past, 16:00–23:00 UTC, weekdays.' },
                { job: 'Catalog refresh (Envoy1)', rule: '[redacted]', cadence: 'cron(0 13 * * ? *)', desc: 'Refresh the Envoy1 catalog list daily at 13:00 UTC.' },
                { job: 'Catalog refresh (Envoy2)', rule: '[redacted]', cadence: 'cron(0 14 * * ? *)', desc: 'Refresh the Envoy2 (distributor) catalog list daily at 14:00 UTC.' },
                { job: 'Account create / update jobs', rule: '— (on demand)', cadence: 'API-triggered', desc: 'Run when an account is imported/edited in the CRM; not scheduled.' }
            ],
            exportFields: [
                { from: 'Order Number', to: 'reference', note: 'Envoy order ref (MTD-####).' },
                { from: 'PO', to: 'customerOrderNo', note: 'Dealer purchase order number.' },
                { from: 'Customer Code', to: 'memberId', note: 'Cin7 contact id. Special case: one customer ([redacted]) is re-resolved by shipping address.' },
                { from: 'Customer Name', to: 'company / billing & delivery names', note: '' },
                { from: 'Ship To Address Line 1/2, City, State, PostalCode, Country', to: 'delivery* fields', note: 'Note the CSV key is "Ship To PostalCode" (no space).' },
                { from: 'Customer Address Line 1/2, City, State, Postal Code, Country', to: 'billing* fields', note: '' },
                { from: 'Internal Notes', to: 'internalComments', note: '' },
                { from: 'Notes', to: 'deliveryInstructions', note: '' },
                { from: 'Shipping Total', to: 'freightTotal', note: 'Freight amount from Envoy.' },
                { from: 'Shipping Name', to: 'freightDescription', note: 'Shipping method label.' },
                { from: 'Discount Name', to: 'discountDescription', note: '' },
                { from: 'Requested Ship Date', to: 'createdDate / estimatedDeliveryDate', note: 'Parsed and converted MST → UTC.' },
                { from: 'Order Date', to: 'customFields.[redacted]', note: 'Submission date.' },
                { from: 'Collection Code', to: 'customFields.[redacted]', note: 'Catalog id; resolved against the catalog list to pick branch & preorder flag.' },
                { from: '(internal DB) shipping account', to: 'customFields.[redacted]', note: 'Shipping account, looked up by Customer Code.' },
                { from: '(internal DB) sales rep agency', to: 'customFields.[redacted]', note: 'External sales rep.' },
                { from: '(derived) preseason check', to: 'customFields.[redacted]', note: 'Yes/No preorder flag.' },
                { from: '(constant)', to: 'stage = "For Review", isApproved = true, createdBy = [redacted]', note: 'Matador Orders user.' }
            ],
            exportLineFields: [
                { from: 'Product SKU Code', to: 'code', note: '' },
                { from: 'Product SKU Quantity', to: 'qty', note: '' },
                { from: 'Product Price', to: 'unitPrice', note: '' },
                { from: '(derived)', to: 'discount', note: 'qty × price × order discount %, where % = Total Discount ÷ Order Total.' }
            ],
            updateFields: [
                { from: 'order.reference', to: 'Order Number', note: '' },
                { from: 'order.reference', to: 'External Order Number', note: 'Same value as Order Number.' },
                { from: 'order.customerOrderNo', to: 'PO', note: 'Dealer purchase order number.' },
                { from: 'customFields.[redacted]', to: 'Order Date', note: 'Submission date, formatted to MST (MM/DD/YYYY).' },
                { from: 'memberId', to: 'Customer Code', note: 'order.memberId, except one customer ([redacted]) which is forced to a fixed id.' },
                { from: 'customFields.[redacted]', to: 'Collection Code', note: 'Catalog id. If blank/Y/N: ship ≥ order+150d → preseason catalog, else replenishment catalog ([redacted]).' },
                { from: 'order.company', to: 'Customer Name', note: '' },
                { from: 'memberId + "-1"', to: 'Ship To Code', note: '' },
                { from: 'order.company', to: 'Ship To Name', note: '' },
                { from: 'order.deliveryAddress1', to: 'Ship To Address Line 1', note: '' },
                { from: 'order.deliveryAddress2', to: 'Ship To Address Line 2', note: '' },
                { from: 'order.deliveryCity', to: 'Ship To City', note: '' },
                { from: 'order.deliveryState', to: 'Ship To State', note: '' },
                { from: 'order.deliveryPostalCode', to: 'Ship To Postal Code', note: '' },
                { from: 'order.deliveryCountry', to: 'Ship To Country', note: '' },
                { from: '"No" (constant)', to: 'Is Drop Ship', note: 'Always "No".' },
                { from: '(computed)', to: 'Status', note: 'order.status VOID → Cancelled; stage Dispatched → Shipped; otherwise Ordered.' },
                { from: '"USD" (constant)', to: 'Currency Code', note: 'Always "USD".' },
                { from: 'order.total + totalDiscount', to: 'Order Total', note: 'Net total plus the summed discount.' },
                { from: 'order.total', to: 'Order Net Total', note: '' },
                { from: 'order.createdDate', to: 'Requested Ship Date', note: 'Formatted to MST (MM/DD/YYYY).' },
                { from: 'order.billingAddress1', to: 'Customer Address Line 1', note: '' },
                { from: 'order.billingAddress2', to: 'Customer Address Line 2', note: '' },
                { from: 'order.billingCity', to: 'Customer City', note: '' },
                { from: 'order.billingState', to: 'Customer State', note: '' },
                { from: 'order.billingPostalCode', to: 'Customer Postal Code', note: '' },
                { from: 'order.billingCountry', to: 'Customer Country', note: '' },
                { from: 'order.internalComments', to: 'Internal Notes', note: 'Carriage returns / line breaks stripped.' },
                { from: 'order.memberId + "-1"', to: 'Ship Code', note: 'Uses raw order.memberId (not the customer override).' },
                { from: 'order.freightTotal', to: 'Shipping', note: 'Freight write-back — Envoy ignores this on submitted orders (see Known Issues).', flag: true },
                { from: 'max(order.discountTotal, Σ line discounts)', to: 'Total Discount', note: 'Whichever is larger.' },
                { from: 'order.discountDescription', to: 'Discount Code', note: 'Falls back to literal "DISCOUNT" when empty.' },
                { from: 'order.discountDescription', to: 'Discount Name', note: 'Same value as Discount Code; falls back to "DISCOUNT".' },
                { from: 'order.invoiceDate', to: 'Is Invoiced', note: 'null → "No", otherwise "Yes".' },
                { from: 'order.invoiceNumber', to: 'Invoice Number', note: '' },
                { from: '(status === Shipped)', to: 'Is Fulfilled', note: '"Yes" when Shipped, else "No".' },
                { from: 'order.invoiceNumber', to: 'Fulfillment Number', note: 'Same value as Invoice Number.' },
                { from: 'line.code', to: 'Product Code', note: 'Line-item SKU code.' },
                { from: 'line.name + " - " + line.option1', to: 'Product Name', note: '' },
                { from: 'line.code', to: 'Product SKU Code', note: 'Same value as Product Code.' },
                { from: 'line.qty', to: 'Product SKU Quantity', note: '' },
                { from: '(status === Shipped) ? line.qty : 0', to: 'Product SKU Shipped Quantity', note: 'Line qty only when Shipped, else 0.' },
                { from: 'line.unitPrice', to: 'Product Price', note: '' }
            ],
            customFields: [
                { id: '[redacted]', meaning: 'External sales rep (Agency name).', setBy: 'Order-import job (from the Matador database).' },
                { id: '[redacted]', meaning: 'Shipping account (carrier acct # or "Y").', setBy: 'Order-import job (from the Matador database).' },
                { id: '[redacted]', meaning: 'Order submission date.', setBy: 'Order-import job.' },
                { id: '[redacted]', meaning: 'Deep-link back to the internal order tooling.', setBy: 'Order-import job (post-create update).' },
                { id: '[redacted]', meaning: 'Catalog / collection id.', setBy: 'Both directions.' },
                { id: '[redacted]', meaning: 'Preorder flag (Yes/No).', setBy: 'Order-import job.' }
            ],
            accountLambdas: [
                { name: 'Account create job', trigger: 'CRM "Import to Envoy" (single id) or bulk.', does: 'Builds customer CSV → /sftp/CUSTOMER/customer_batch_#.csv; tracks a multi-step progress record.' },
                { name: 'Account update job', trigger: 'CRM account edit.', does: 'Pushes updated customer details (incl. discount code) → /sftp/CUSTOMER/customer_update.csv.' },
                { name: 'Bulk order seed job', trigger: 'Manual / seed.', does: 'Bulk historical order export → /sftp/INBOUND_ORDERS/inboundorders_upload.csv.' }
            ],
            issues: [
                {
                    title: 'Freight changes in Cin7 do NOT reach Envoy',
                    icon: 'mdi-truck-alert', color: 'amber darken-2',
                    body: 'Confirmed by live E2E testing on order MTD-1602 (Jun 2026). The write-back feed does send freight in the <code>Shipping</code> column, and the file ingests with no errors, but Envoy keeps its own <strong>method-based</strong> shipping (e.g. "FedEx Ground (USA)", $119) and does not overwrite it from the inbound flat amount on a submitted order. Renaming the column (tried <code>Shipping Total</code> and adding <code>Shipping Name</code>) made no difference. <strong>Quantity updates do flow through</strong> — only freight is ignored. We would like Envoy to confirm whether, and how, shipping is updatable on a submitted order via the inbound feed (or whether it must be done through the Envoy UI / API).'
                }
            ]
        }
    }
}
</script>

<style scoped>
/* Self-contained wrapper — deliberately NOT .v-container/.col, which a global
   (unscoped) rule in salesRepList.vue forces to display:flex app-wide. */
.envoy-doc {
    max-width: 1100px;
    margin: 0 auto;
    padding: 24px 12px;
}
.envoy-doc__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
}
.envoy-doc__header-text {
    flex: 1 1 auto;
    min-width: 0;
}
code {
    background: #f4f4f4;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 0.85em;
    color: #c7254e;
    box-shadow: none;
}
.print-only {
    display: none;
}
</style>

<style>
/* Print / "Save as PDF" formatting — strips app chrome and opens accordions */
@media print {
    .no-print { display: none !important; }
    .print-only { display: block !important; }
    .v-application .v-app-bar,
    .v-application .v-navigation-drawer,
    .v-application .v-footer,
    .v-application .v-snack { display: none !important; }
    .v-main { padding: 0 !important; }
    .v-card { box-shadow: none !important; border: 1px solid #ddd !important; page-break-inside: avoid; }
    .v-expansion-panel-content { display: block !important; }
    a { color: inherit !important; text-decoration: none !important; }
}
</style>
